import { useCallback, useEffect, useState } from "react";
import GetCoordinatesApi from "./getCoordinatesApi";
import { useAppSelector } from "app/hooks";
import { notNullNorUndefined } from "shared/helpers/notNullNorUndefined";

type CalculatedCoordinates =
  | {
      message: string;
    }
  | {
      lat: number;
      lng: number;
      altitude: number;
    }
  | null;

type UseGetCoordinates = () => [CalculatedCoordinates, VoidFunction];

export const useGetCoordinates: UseGetCoordinates = () => {
  const [calculatedCoordinates, setCalculatedCoordinates] =
    useState<CalculatedCoordinates>(null);

  const closePanel = useCallback((fromKrpano = false) => {
    setCalculatedCoordinates(null);
    if (!fromKrpano) {
      window.krpano.call("closeGPSCoordinates();");
    }
  }, []);

  const guid = useAppSelector((state) => state.panorama.guid);
  const currentFieldId = useAppSelector(
    (state) => state.panorama.currentFieldDetails?.fid
  );
  const droneLat = useAppSelector(
    (state) => state.panorama.currentFieldDetails?.lat
  );
  const droneLng = useAppSelector(
    (state) => state.panorama.currentFieldDetails?.lng
  );
  const droneAltitude = useAppSelector(
    (state) => state.panorama.currentFieldDetails?.drone_altitude
  );
  const orientationAngle = useAppSelector(
    (state) => state.panorama.currentFieldDetails?.orientation_angle
  );
  useEffect(() => {
    const getCoordinatesHandler = async (
      event: WindowEventMap["krpano__get_coordinates"]
    ) => {
      if (
        notNullNorUndefined(event.detail) &&
        notNullNorUndefined(event.detail.ath) &&
        notNullNorUndefined(event.detail.atv) &&
        notNullNorUndefined(droneLat) &&
        notNullNorUndefined(droneLng) &&
        notNullNorUndefined(droneAltitude) &&
        notNullNorUndefined(currentFieldId) &&
        notNullNorUndefined(guid)
      ) {
        const ath = event.detail.ath;
        const atv = event.detail.atv;
        setCalculatedCoordinates({
          message: "Calculating coordinates...",
        });
        const response = await GetCoordinatesApi.getCoordinates({
          ath:
            notNullNorUndefined(ath) && notNullNorUndefined(orientationAngle)
              ? ath + parseFloat(orientationAngle)
              : ath,
          atv: atv,
          drone_lat: droneLat,
          drone_lon: droneLng,
          drone_alt: droneAltitude,
          fieldId: currentFieldId,
          panoGuid: guid,
        });
        if (response.success && response.data) {
          // position: "[<<lng>>,<<lat>>,<altitude>]"
          // position: "[-1.9422126689649242,55.50146585360233,76.3]"
          const [lat, lng, altitude] = JSON.parse(response.data.position);
          setCalculatedCoordinates({
            lat: lat,
            lng: lng,
            altitude: altitude,
          });
        } else {
          console.warn("Error getting coordinates", response.errors);
          setCalculatedCoordinates({
            message: `An error occurred loading coordinates${
              response.errors ? `: ${response.errors}` : "."
            }`,
          });
        }
      } else {
        console.warn("Possible missing data, check if any of these are null", {
          eventDetails: event.detail,
          droneLat,
          droneLng,
          droneAltitude,
          currentFieldId,
          guid,
        });
        setCalculatedCoordinates({
          message: "Data missing to calculate coordinates. Please try again.",
        });
      }
    };
    window.addEventListener("krpano__get_coordinates", getCoordinatesHandler);
    return () => {
      window.removeEventListener(
        "krpano__get_coordinates",
        getCoordinatesHandler
      );
    };
  }, [
    currentFieldId,
    droneAltitude,
    droneLat,
    droneLng,
    guid,
    orientationAngle,
  ]);

  // Close coordinates panel when krpano sends close event
  useEffect(() => {
    const closeCoordinatesHandler = async (
      event: WindowEventMap["krpano__get_coordinates_close"]
    ) => {
      closePanel(true);
    };
    window.addEventListener(
      "krpano__get_coordinates_close",
      closeCoordinatesHandler
    );
    return () => {
      window.removeEventListener(
        "krpano__get_coordinates_close",
        closeCoordinatesHandler
      );
    };
  }, [closePanel]);

  return [calculatedCoordinates, closePanel];
};
